import React, { useState, useRef, useEffect, useContext } from 'react';
import Layout from '../Layout/Layout';
import { useTranslation, useI18next } from 'gatsby-plugin-react-i18next';
import {
    Container,
    HeaderText,
    ContentWrapper,
    SearchWrapper,
    Select,
    ProductBox,
    SearchFrom,
    ProductList,
    ProductListOption,
    InputSearchWithIcon,
    InputSearch,
    SearchIcon,
    SelectDivider,
    InputSearchWithIconWrapper,
    Card,
    CardHeader,
    CardImageWrapper,
    CardWrapper,
    ResultsWrapper,
    ResultsTitle,
    ResultsNumber,
    MobileResultWrapper,
    ResetButton,
} from './ProductsSearchStyle';
import Header from '../HeaderDynamicPages/Header';
import { StaticImage } from 'gatsby-plugin-image';
import ProductsResultView from './ProductsResultView/ProductsResultView';
import Loader from '../Loader/Loader';
import { categoryData } from './JSON/categoryStaticData';
import { ProductSearchResultsContext } from '../../context/ProductSearchResultsContext';
import { dataToBreadcrumbsFromNav } from '../../utils/dataToBreadCrumbsFromNav';
import searchProductsTranslations from '../../JSON/StaticSitesTranslations/searchProductsTranslations.json';
import { DE, ZH_HANS } from '../../constants/languageCode';
import { getSiteName } from '../../utils/getSiteName';

const ProductsSearch = ({ schema: { main } }) => {
    const { t } = useTranslation();
    const { language } = useI18next();
    const [isActiveList, setIsActiveList] = useState(false);
    const {
        isLoading,
        numberOfResults,
        currentCategoryName,
        setCurrentCategoryName,
        setCurrentCategoryId,
        setSearchFilter,
        setIsFiltersSubmit,
        isMobileView,
        setTypeFilter,
        setModeFilter,
        setPressureRangeFilter,
        setHeatRecoveryFilter,
        setSoundAbsorptionFilter,
        setDeliveryControlFilter,
        setCoolingFilter,
        setOtherFilter,
        setFedMinFilter,
        setFedMaxFilter,
        setMotorPowerMinFilter,
        setMotorPowerMaxFilter,
        setCompressionModeUnits,
        isProductsSearchFiltersOpened,
        setCategoryNamesWithId,
        motorPowerMaxValue,
        fedMaxValue,
    } = useContext(ProductSearchResultsContext);
    const [isClicked, setIsClicked] = useState(false);
    const searchListElement = useRef(null);
    const searchFieldValue = useRef(null);
    const listItemId = 0;
    const listItemValue = 1;
    const numberOfItems = 16;
    const { category, categoryItem } = dataToBreadcrumbsFromNav(language, t('products').toLocaleLowerCase());
    if (category) {
        category.name = t('product');
    }

    const pairCategoryNameWithId = (): void => {
        const categoryNamesWithId = [];
        categoryData?.forEach((item: any, index: number) => {
            categoryNamesWithId.push({ name: item.name, id: item.id });
        });
        setCategoryNamesWithId(categoryNamesWithId);
    };

    useEffect(() => {
        pairCategoryNameWithId();
    }, []);

    useEffect(() => {
        function handleClickOutsideList(event: any) {
            if (searchListElement.current && !searchListElement.current.contains(event.target)) {
                setIsActiveList(false);
            }
        }
        typeof document !== 'undefined' ? document.addEventListener('mousedown', handleClickOutsideList) : '';
        return () => {
            typeof document !== 'undefined' ? document.removeEventListener('mousedown', handleClickOutsideList) : '';
        };
    }, [searchListElement]);

    const displayListItems = () =>
        Object.entries(main)?.map((item) => displayListItem(item[listItemValue], item[listItemId]));

    const displayListItem = (item: any, id) => (
        <ProductListOption
            data-value={id}
            key={item}
            onClick={(e) => {
                setCurrentCategoryId(e.currentTarget.getAttribute('data-value'));
                setIsActiveList((isActiveList) => !isActiveList);
                setIsClicked(true);
                setCurrentCategoryName(item.toLowerCase().replace(/\s/g, '.'));
                setIsFiltersSubmit(true);
            }}
        >
            {t(item.toLowerCase().replace(/\s/g, '.'))}
        </ProductListOption>
    );

    const displayStaticImage = (name: string) => {
        if (name === 'compressors') {
            return <StaticImage src="../../static/images/productSearch/prod-search-gal-img-1.png" alt={name} />;
        } else if (name === 'compressed.air.treatment') {
            return <StaticImage src="../../static/images/productSearch/prod-search-gal-img-2.png" alt={name} />;
        } else if (name === 'special.gases') {
            return (
                <StaticImage src="../../static/images/productSearch/prod-search-gal-img-3.png" alt="special gases" />
            );
        } else if (name === 'controls') {
            return <StaticImage src="../../static/images/productSearch/prod-search-gal-img-4.png" alt="controls" />;
        }
    };

    const displayCardWrapper = () => (
        <CardWrapper>
            {categoryData.map((item: any, index: number) => (
                <Card
                    key={index}
                    onClick={() => {
                        setIsClicked(true);
                        setCurrentCategoryName(item.name);
                        setCurrentCategoryId(item.id);
                        setIsFiltersSubmit(true);
                    }}
                >
                    <CardHeader>{t(`${item.name}`)}</CardHeader>
                    <CardImageWrapper>{displayStaticImage(item.name)}</CardImageWrapper>
                </Card>
            ))}
        </CardWrapper>
    );
    const handleSubmit = (e: any) => {
        e.preventDefault();
        setSearchFilter(searchFieldValue.current.value);
        setIsClicked(true);
        setIsFiltersSubmit(true);
        searchFieldValue.current.value = '';
    };

    const clearAllFilters = () => {
        setTypeFilter('');
        setModeFilter('');
        setPressureRangeFilter(0);
        setSearchFilter('');
        setHeatRecoveryFilter(0);
        setSoundAbsorptionFilter([]);
        setDeliveryControlFilter([]);
        setCoolingFilter([]);
        setOtherFilter([]);
        setCurrentCategoryName('');
        setCurrentCategoryId('');
        setFedMinFilter(0);
        setFedMaxFilter(fedMaxValue);
        setMotorPowerMinFilter(0);
        setMotorPowerMaxFilter(motorPowerMaxValue);
        setCompressionModeUnits('');
        setIsFiltersSubmit(true);
        setIsClicked(true);
    };

    const displaySearchForm = () => {
        if (!isMobileView) {
            return (
                <SearchWrapper>
                    <SearchFrom ref={searchListElement}>
                        <Select onClick={() => setIsActiveList((isActiveList) => !isActiveList)}>
                            {currentCategoryName ? t(`${currentCategoryName}`) : t('pleaseMakeSelection')}
                        </Select>
                        <ProductBox isActive={isActiveList}>
                            <ProductList>{displayListItems()}</ProductList>
                        </ProductBox>
                    </SearchFrom>
                    {numberOfResults > 0 && (
                        <ResultsWrapper isFixed={!isMobileView}>
                            <ResultsTitle>
                                <ResultsNumber>{numberOfResults}</ResultsNumber> {t('results')}
                            </ResultsTitle>
                        </ResultsWrapper>
                    )}
                    <InputSearchWithIconWrapper onSubmit={handleSubmit}>
                        <InputSearchWithIcon>
                            <InputSearch placeholder={t('inputSearch')} ref={searchFieldValue} />
                            <SelectDivider />
                            <SearchIcon onClick={handleSubmit} type="submit" />
                        </InputSearchWithIcon>
                    </InputSearchWithIconWrapper>
                </SearchWrapper>
            );
        } else {
            return (
                <MobileResultWrapper>
                    <ResetButton onClick={clearAllFilters}>{t('reset')}</ResetButton>
                    <ResultsWrapper isFixed={!isMobileView}>
                        <ResultsTitle>
                            <ResultsNumber>{numberOfResults}</ResultsNumber> {t('results')}
                        </ResultsTitle>
                    </ResultsWrapper>
                </MobileResultWrapper>
            );
        }
    };

    return (
        <>
            {isLoading && isClicked && <Loader />}
            <Layout
                title={
                    language === DE
                        ? 'BOGE | Produktsuche | Druckluft | Kompressoren'
                        : `${getSiteName(language)} | ${t('searchProducts')}`
                }
                description={
                    language === DE
                        ? 'Finden Sie mithilfe der BOGE Produktsuche den passenden Kompressor sowie Druckluft-Zubehör und Kompressor-Steuerungen für Ihren Bedarf. Jetzt online Kompressoren finden.'
                        : ''
                }
                languageVersionsUrl={searchProductsTranslations}
            >
                {language === ZH_HANS ? (
                    <Header category={{ name: '产品', url: '/zh-hans/products-overview/' }} item={'ProduktSuche'} />
                ) : (
                    <Header category={category} categoryItem={categoryItem} />
                )}
                <Container>
                    <ContentWrapper>
                        <HeaderText>{t('whichProductAreYouLookingFor')}?</HeaderText>
                        {displaySearchForm()}
                        {isClicked || isProductsSearchFiltersOpened ? (
                            <ProductsResultView numberOfItems={numberOfItems} />
                        ) : (
                            displayCardWrapper()
                        )}
                    </ContentWrapper>
                </Container>
            </Layout>
        </>
    );
};

export default ProductsSearch;
